/**
 * OverviewPage — the lab-admin "Tableau de bord": KPIs aggregated from the cockpit's own endpoints,
 * each tile/section drawn ONLY when its capability is held (the queries run gated on `can && online`),
 * plus quick links to the other sections. There is no dedicated overview endpoint — the overview is a
 * composition of the per-section reads, so a manager with partial rights sees a partial (never broken)
 * board. Full 5-state DoD per data source; a defensive ForbiddenState covers a user with NONE of the caps.
 */
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, ForbiddenState, OfflineState } from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ADMIN_CAPS } from "../caps";
import { useLabId, useStaff, usePricing, useSubscription, useJournal, useSyncHealth } from "../api";
import type { SyncEtat } from "../types";
import { PageHeader } from "../components/PageHeader";
import { KpiTile } from "../components/KpiTile";
import { Screen, TileGrid } from "../components/Screen";
import { formatDateTime, statutTone } from "../lib";

/** Badge tone per master-data sync etat (frais → ok, en_grace → warn, perime → danger). */
const syncTone = (etat: SyncEtat | undefined): "success" | "warn" | "danger" | "neutral" => {
  if (etat === "frais") return "success";
  if (etat === "en_grace") return "warn";
  if (etat === "perime") return "danger";
  return "neutral";
};

export function OverviewPage() {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);

  const canStaff = useCan(ADMIN_CAPS.staffManage);
  const canPricing = useCan(ADMIN_CAPS.pricing);
  const canSubscription = useCan(ADMIN_CAPS.abonnementRead);
  const canJournal = useCan(ADMIN_CAPS.journalReadAll);
  const canProfile = useCan(ADMIN_CAPS.profilManage);

  const anyCap = canStaff || canPricing || canSubscription || canJournal || canProfile;

  // Each source runs only when its cap is held + online — partial rights = partial board.
  const staff = useStaff(labId, canStaff && online);
  const pricing = usePricing(labId, canPricing && online);
  const subscription = useSubscription(labId, canSubscription && online);
  const journal = useJournal(labId, { page: 1, page_size: 1 }, canJournal && online);
  // NOVA-063 — master-data sync health (same read cap as the subscription view).
  const syncHealth = useSyncHealth(labId, canSubscription && online);
  const sync = syncHealth.data;

  const staffRows = staff.data ?? [];
  const owners = staffRows.filter((s) => s.estProprietaire).length;

  const header = (
    <PageHeader title={t("admin.overview.title")} subtitle={t("admin.overview.subtitle")} />
  );

  if (!anyCap) return <ForbiddenState requiredCapabilities={[ADMIN_CAPS.profilRead]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void Promise.all([staff.refetch(), subscription.refetch()])} />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      {/* NOVA-063 — the perime banner: master-data stale past grace. Cloud caps suspend; care/billing run. */}
      {canSubscription && sync?.etat === "perime" && (
        <Card>
          <div role="alert" style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
            <strong style={{ color: "var(--nv-danger, var(--nv-critical))" }}>
              {t("admin.overview.sync.perime.title")}
            </strong>
            <span style={{ color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("admin.overview.sync.perime.desc", { graceDays: sync.graceDays })}
            </span>
          </div>
        </Card>
      )}

      <TileGrid>
        {canStaff && (
          <>
            <KpiTile
              label={t("admin.overview.kpi.staff")}
              value={staff.isLoading ? "…" : staffRows.length}
              hint={t("admin.overview.kpi.staff.hint")}
              tone="accent"
              icon="◉"
            />
            <KpiTile
              label={t("admin.overview.kpi.owners")}
              value={staff.isLoading ? "…" : owners}
              tone="neutral"
              icon="★"
            />
          </>
        )}
        {canPricing && (
          <KpiTile
            label={t("admin.overview.kpi.priced")}
            value={pricing.isLoading ? "…" : (pricing.data ?? []).length}
            hint={t("admin.overview.kpi.priced.hint")}
            tone="neutral"
            icon="₪"
          />
        )}
        {canSubscription && (
          <>
            <KpiTile
              label={t("admin.overview.kpi.plan")}
              value={subscription.isLoading ? "…" : (subscription.data?.plan ?? "—")}
              tone="accent"
              icon="◆"
            />
            <KpiTile
              label={t("admin.overview.kpi.statut")}
              value={
                subscription.isLoading ? (
                  "…"
                ) : (
                  <Badge tone={statutTone(subscription.data?.statut)} glyph="●">
                    {subscription.data?.statut ?? t("admin.subscription.health.notSynced")}
                  </Badge>
                )
              }
            />
          </>
        )}
        {canJournal && (
          <KpiTile
            label={t("admin.overview.kpi.audit")}
            value={journal.isLoading ? "…" : (journal.data?.total ?? 0)}
            hint={t("admin.overview.kpi.audit.hint")}
            tone="neutral"
            icon="▤"
          />
        )}
        {canSubscription && (
          <KpiTile
            label={t("admin.overview.sync.kpi")}
            value={
              syncHealth.isLoading || !sync ? (
                "…"
              ) : (
                <Badge tone={syncTone(sync.etat)} glyph="●">
                  {t(`admin.overview.sync.etat.${sync.etat}`)}
                </Badge>
              )
            }
            hint={t("admin.overview.kpi.statut")}
          />
        )}
      </TileGrid>

      {canSubscription && subscription.data && (
        <Card title={t("admin.overview.health.title")}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
            <Badge tone={subscription.data.health.active ? "success" : "warn"} glyph="●">
              {subscription.data.health.active
                ? t("admin.subscription.health.active")
                : t("admin.subscription.health.inactive")}
            </Badge>
            <Badge tone={subscription.data.health.synced ? "info" : "neutral"} glyph="◇">
              {subscription.data.health.synced
                ? t("admin.subscription.health.synced")
                : t("admin.subscription.health.notSynced")}
            </Badge>
            <span style={{ color: "var(--nv-text-2, var(--nv-slate))" }}>
              {subscription.data.health.message}
            </span>
          </div>
        </Card>
      )}

      {/* NOVA-063 — the sync-health detail (etat + last-sync + grace + import version). */}
      {canSubscription && sync && (
        <Card title={t("admin.overview.sync.title")}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-3)", flexWrap: "wrap", marginBottom: "var(--nv-s-3)" }}>
            <Badge tone={syncTone(sync.etat)} glyph="●">
              {t(`admin.overview.sync.etat.${sync.etat}`)}
            </Badge>
            {sync.etat === "en_grace" && (
              <span style={{ color: "var(--nv-text-2, var(--nv-slate))" }}>
                {t("admin.overview.sync.grace.desc")}
              </span>
            )}
          </div>
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-4)", margin: 0 }}>
            <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{t("admin.overview.sync.lastSync")}</dt>
            <dd style={{ margin: 0 }} className="nv-ltr-island">
              {sync.derniereSynchroAt ? formatDateTime(sync.derniereSynchroAt, locale) : t("admin.overview.sync.lastSync.never")}
            </dd>
            {sync.graceExpireAt && (
              <>
                <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{t("admin.overview.sync.graceExpire")}</dt>
                <dd style={{ margin: 0 }} className="nv-ltr-island">{formatDateTime(sync.graceExpireAt, locale)}</dd>
              </>
            )}
            {sync.versionImportCourante !== null && (
              <>
                <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{t("admin.overview.sync.version")}</dt>
                <dd style={{ margin: 0 }} className="nv-ltr-island">{sync.versionImportCourante}</dd>
              </>
            )}
          </dl>
        </Card>
      )}

      <Card title={t("admin.overview.quick.title")}>
        <div style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
          {canProfile && (
            <Button variant="secondary" onClick={() => navigate("/admin/profil")}>
              {t("admin.overview.quick.profile")}
            </Button>
          )}
          {canStaff && (
            <Button variant="secondary" onClick={() => navigate("/admin/equipe")}>
              {t("admin.overview.quick.team")}
            </Button>
          )}
          {canJournal && (
            <Button variant="secondary" onClick={() => navigate("/admin/journal")}>
              {t("admin.overview.quick.journal")}
            </Button>
          )}
          {canPricing && (
            <Button variant="secondary" onClick={() => navigate("/admin/tarification")}>
              {t("admin.overview.quick.pricing")}
            </Button>
          )}
          {canSubscription && (
            <Button variant="secondary" onClick={() => navigate("/admin/abonnement")}>
              {t("admin.overview.quick.subscription")}
            </Button>
          )}
        </div>
        {!(canStaff && canPricing && canSubscription && canJournal && canProfile) && (
          <p style={{ marginTop: "var(--nv-s-3)", color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
            {t("admin.overview.restricted")}
          </p>
        )}
      </Card>
    </Screen>
  );
}
